/**
 * src/pages/venue/InflowPage.tsx — 2304vc
 *
 * Inflow source management for venue staff. Allows connecting
 * OneDrive, Google Drive, Dropbox, or a local folder as media
 * sources that feed the picker queue.
 *
 * Each connector:
 *   1. OAuth button → redirects to provider auth page
 *   2. On return from callback → shows connected state
 *   3. Folder picker → select which folder to watch
 *   4. Active/paused toggle
 *   5. Last sync time + file count
 *
 * Local folder uses the File System Access API (no OAuth needed).
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FolderOpen, RefreshCw, Pause, Play } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/components/ui/utils'
import type { Venue } from '@/types'
import type { PickerSession } from '@/lib/venue'

// ── Types ──────────────────────────────────────────────────────────────────

type SourceType = 'onedrive' | 'gdrive' | 'dropbox' | 'local'

interface IngestSource {
  id:           string
  venue_id:     string
  source_type:  SourceType
  // display_name not in schema
    // name: string
  active:       boolean
  config:       {
    connected_at?: string
    folder_path?:  string
    folder_name?:  string
    last_sync?:    string
    file_count?:   number
  }
}

// ── OAuth URL builders ──────────────────────────────────────────────────────

const ONEDRIVE_CLIENT_ID = import.meta.env.VITE_ONEDRIVE_CLIENT_ID
const ONEDRIVE_TENANT_ID = import.meta.env.VITE_ONEDRIVE_TENANT_ID
const GDRIVE_CLIENT_ID   = import.meta.env.VITE_GDRIVE_CLIENT_ID
const DROPBOX_APP_KEY    = import.meta.env.VITE_DROPBOX_APP_KEY

function buildOneDriveAuthUrl(venueSlug: string): string {
  const redirectUri = `${window.location.origin}/auth/onedrive/callback`
  const params = new URLSearchParams({
    client_id:     ONEDRIVE_CLIENT_ID,
    response_type: 'code',
    redirect_uri:  redirectUri,
    scope:         'Files.Read Files.Read.All offline_access',
    response_mode: 'query',
    state:         venueSlug,
  })
  return `https://login.microsoftonline.com/${ONEDRIVE_TENANT_ID}/oauth2/v2.0/authorize?${params}`
}

function buildGDriveAuthUrl(venueSlug: string): string {
  const redirectUri = `${window.location.origin}/auth/gdrive/callback`
  const params = new URLSearchParams({
    client_id:     GDRIVE_CLIENT_ID,
    response_type: 'code',
    redirect_uri:  redirectUri,
    scope:         'https://www.googleapis.com/auth/drive.readonly',
    access_type:   'offline',
    prompt:        'consent',
    state:         venueSlug,
  })
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

function buildDropboxAuthUrl(venueSlug: string): string {
  const redirectUri = `${window.location.origin}/auth/dropbox/callback`
  const params = new URLSearchParams({
    client_id:     DROPBOX_APP_KEY,
    response_type: 'code',
    redirect_uri:  redirectUri,
    token_access_type: 'offline',
    state:         venueSlug,
  })
  return `https://www.dropbox.com/oauth2/authorize?${params}`
}

// ── Data ────────────────────────────────────────────────────────────────────

async function fetchIngestSources(venueId: string): Promise<IngestSource[]> {
  const { data, error } = await supabase
    .from('ingest_sources')
    .select('id, venue_id, source_type, credentials_ref, active, config')
    .eq('venue_id', venueId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as unknown as IngestSource[]
}

async function toggleSource(sourceId: string, isActive: boolean) {
  const { error } = await supabase
    .from('ingest_sources')
    .update({ is_active: isActive })
    .eq('id', sourceId)
  if (error) throw error
}

// ── Provider card ───────────────────────────────────────────────────────────

interface ProviderConfig {
  type:     SourceType
  label:    string
  sub:      string
  icon:     string
  color:    string
  buildUrl: ((slug: string) => string) | null
}

const PROVIDERS: ProviderConfig[] = [
  {
    type: 'onedrive', label: 'OneDrive', sub: 'Microsoft / SharePoint',
    icon: '☁', color: '#0078D4',
    buildUrl: buildOneDriveAuthUrl,
  },
  {
    type: 'gdrive', label: 'Google Drive', sub: 'Google Workspace',
    icon: '🗂', color: '#4285F4',
    buildUrl: buildGDriveAuthUrl,
  },
  {
    type: 'dropbox', label: 'Dropbox', sub: 'App folder — isolated',
    icon: '📦', color: '#0061FF',
    buildUrl: buildDropboxAuthUrl,
  },
  {
    type: 'local', label: 'Local folder', sub: 'This device — no account needed',
    icon: '💾', color: '#888888',
    buildUrl: null,
  },
]

function ConnectorCard({
  provider,
  source,
  venueId,
  venueSlug,
  onLocalConnect,
  onToggle,
  onChooseFolder,
  onSync,
  onAiTag,
  loading = false,
}: {
  provider:        ProviderConfig
  source:          IngestSource | null
  venueId:         string
  venueSlug:       string
  onLocalConnect:  () => void
  onToggle:        (sourceId: string, active: boolean) => void
  onChooseFolder:  (source: IngestSource) => void
  onSync:          () => void
  onAiTag:         () => void
  loading?:        boolean
}) {
  const connected = source !== null && !!(source?.config as Record<string,unknown>)?.tokens
  const isActive  = source?.active ?? false

  function handleConnect() {
    if (provider.type === 'local') {
      onLocalConnect()
      return
    }
    // Store venue context for the callback to use
    sessionStorage.setItem('oauth_venue_id',   venueId)
    sessionStorage.setItem('oauth_venue_slug',  venueSlug)
    window.location.href = provider.buildUrl!(venueSlug)
  }

  const folderName = source?.config?.folder_name
  const lastSync   = source?.config?.last_sync
    ? new Date(source.config.last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null
  const fileCount = source?.config?.file_count

  return (
    <div className={cn(
      'rounded-2xl border transition-all duration-200',
      connected
        ? 'bg-white/5 border-white/15'
        : 'bg-white/3 border-white/8',
    )}>
      <div className="flex items-center gap-3 p-4">
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
          style={{ backgroundColor: `${provider.color}18`, border: `1px solid ${provider.color}30` }}>
          {provider.icon}
        </div>

        {/* Labels */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-semibold text-[14px]">{provider.label}</p>
            {connected && isActive && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            )}
            {connected && !isActive && (
              <span className="text-[10px] text-white/30 font-medium">Paused</span>
            )}
          </div>
          <p className="text-white/40 text-[11px]">{provider.sub}</p>
          {connected && folderName && (
            <p className="text-white/50 text-[11px] mt-0.5 flex items-center gap-1">
              <FolderOpen size={9} />
              {folderName}
            </p>
          )}
        </div>

        {/* Action */}
        <div className="shrink-0 flex items-center gap-2">
          {loading ? (
            <div className="w-16 h-7 rounded-xl bg-white/8 animate-pulse" />
          ) : !connected ? (
            <button type="button" onClick={handleConnect}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white
                         border border-white/15 bg-white/8 hover:bg-white/12
                         active:scale-95 transition-all">
              Connect
            </button>
          ) : (
            <>
            <button type="button" onClick={handleConnect}
              className="px-2 py-1 rounded-lg text-[10px] text-white/30 hover:text-white/60
                         border border-white/8 hover:border-white/20 transition-all">
              Reconnect
            </button>
            <button type="button" onClick={() => onToggle(source!.id, !isActive)}
              className={cn(
                'w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-95',
                isActive
                  ? 'bg-white/8 text-white/50 hover:text-amber-400 hover:bg-amber-500/10'
                  : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25',
              )}>
              {isActive ? <Pause size={14} /> : <Play size={14} />}
            </button>
            </>
          )}
        </div>
      </div>

      {/* Choose folder prompt if connected but no folder */}
      {connected && !source?.config?.folder_name && !loading && (
        <div className="px-3 pb-2">
          <button type="button"
            onClick={() => onChooseFolder(source!)}
            className="text-[11px] text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1">
            <FolderOpen size={10} /> Choose a folder to watch
          </button>
        </div>
      )}

      {/* Stats row — only if connected */}
      {connected && (
        <div className="flex items-center gap-4 px-3 pb-2 pt-0">
          {lastSync && (
            <span className="text-[10px] text-white/30 flex items-center gap-1">
              <RefreshCw size={8} />
              {lastSync}
            </span>
          )}
          {fileCount != null && (
            <span className="text-[10px] text-white/30">
              {fileCount} files
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <button type="button"
              onClick={onSync}
              className="text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1">
              <RefreshCw size={8} /> Sync now
            </button>
            <button type="button"
              onClick={onAiTag}
              className="text-[10px] text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1">
              ✦ AI sort
            </button>
            <button type="button"
              className="text-[10px] text-white/25 hover:text-white/50 transition-colors"
              onClick={() => onChooseFolder(source!)}>
              Change folder
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Local folder connector ─────────────────────────────────────────────────

async function connectLocalFolder(venueId: string): Promise<IngestSource | null> {
  // File System Access API
  if (!('showDirectoryPicker' in window)) {
    alert('Local folder access requires Chrome or Edge 86+')
    return null
  }

  let dirHandle: FileSystemDirectoryHandle
  try {
    dirHandle = await (window as any).showDirectoryPicker({ mode: 'read' })
  } catch {
    return null // User cancelled
  }

  const { data, error } = await supabase
    .from('ingest_sources')
    .upsert({
      venue_id:     venueId,
      source_type:  'local',
      // display_name not in schema
    // name: `Local: ${dirHandle.name}`,
      is_active:    true,
      config: {
        folder_name:  dirHandle.name,
        connected_at: new Date().toISOString(),
      },
      credentials_ref: `local_${venueId}`,
    }, { onConflict: 'venue_id,source_type' })
    .select()
    .single()

  if (error) { console.error(error); return null }
  return data as IngestSource
}

// ── Main page ──────────────────────────────────────────────────────────────

// ── Folder picker ─────────────────────────────────────────────────────────

interface FolderItem { id: string; name: string; path: string; hasChildren: boolean }

function FolderPicker({ source, onSelect, onClose }: {
  source:   IngestSource
  onSelect: (folder: FolderItem) => void
  onClose:  () => void
}) {
  const [path, setPath]       = useState('/')
  const [folders, setFolders] = useState<FolderItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [breadcrumb, setBreadcrumb] = useState<Array<{ name: string; path: string }>>([{ name: 'Root', path: '/' }])

  const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL
  const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY

  async function loadFolders(p: string) {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/list-folders?source_id=${source.id}&path=${encodeURIComponent(p)}`,
        { headers: { 'apikey': SUPABASE_ANON, 'Authorization': `Bearer ${SUPABASE_ANON}` } }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Failed to list folders')
      setFolders(data.folders ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load folders')
    }
    setLoading(false)
  }

  useEffect(() => { loadFolders(path) }, [path])

  function navigate(folder: FolderItem) {
    setPath(folder.path)
    setBreadcrumb((prev: Array<{ name: string; path: string }>) => [...prev, { name: folder.name, path: folder.path }])
  }

  function navigateTo(crumb: { name: string; path: string }, idx: number) {
    setPath(crumb.path)
    setBreadcrumb((prev: Array<{ name: string; path: string }>) => prev.slice(0, idx + 1))
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-white/10 shrink-0">
        <button type="button" onClick={onClose}
          className="text-white/50 hover:text-white text-sm transition-colors">
          ← Back
        </button>
        <p className="text-white font-semibold text-sm">Choose folder</p>
        <div className="w-12" />
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto shrink-0" style={{ scrollbarWidth: 'none' }}>
        {breadcrumb.map((crumb: { name: string; path: string }, i: number) => (
          <div key={i} className="flex items-center gap-1 shrink-0">
            {i > 0 && <span className="text-white/25 text-xs">/</span>}
            <button type="button" onClick={() => navigateTo(crumb, i)}
              className={cn(
                'text-xs transition-colors',
                i === breadcrumb.length - 1 ? 'text-white font-medium' : 'text-white/40 hover:text-white/70'
              )}>
              {crumb.name}
            </button>
          </div>
        ))}
      </div>

      {/* Folder list */}
      <div className="flex-1 overflow-y-auto px-4 py-2">
        {loading && (
          <div className="space-y-2 pt-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-12 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        )}
        {error && (
          <div className="text-red-400 text-sm text-center mt-8">{error}</div>
        )}
        {!loading && !error && folders.length === 0 && (
          <div className="text-center mt-8">
            <p className="text-white/40 text-sm">No folders here</p>
            <button type="button" onClick={() => onSelect({ id: path, name: breadcrumb[breadcrumb.length-1].name, path, hasChildren: false })}
              className="mt-4 px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/15 transition-colors">
              Use this folder → start picking
            </button>
          </div>
        )}
        {!loading && !error && folders.map(folder => (
          <div key={folder.id} className="flex items-center gap-3 py-3 border-b border-white/6">
            <span className="text-xl">📁</span>
            <button type="button" onClick={() => navigate(folder)}
              className="flex-1 text-left text-white/80 text-sm hover:text-white transition-colors">
              {folder.name}
            </button>
            <button type="button"
              onClick={() => onSelect(folder)}
              className="px-3 py-1.5 rounded-lg bg-white/10 text-white/70 text-xs font-medium
                         hover:bg-white/20 hover:text-white transition-all active:scale-95">
              Select
            </button>
            <button type="button" onClick={() => navigate(folder)}
              className="text-white/30 hover:text-white/60 transition-colors text-lg leading-none">
              ›
            </button>
          </div>
        ))}
      </div>

      {/* Select current folder */}
      <div className="px-4 py-4 border-t border-white/10 shrink-0">
        <button type="button"
          onClick={() => onSelect({ id: path, name: breadcrumb[breadcrumb.length-1].name, path, hasChildren: true })}
          className="w-full py-3 rounded-2xl bg-white text-black font-bold text-sm
                     hover:bg-white/90 active:scale-98 transition-all">
          Use "{breadcrumb[breadcrumb.length-1].name}"
        </button>
      </div>
    </div>
  )
}

export interface InflowPageProps {
  venue:  Venue
  picker: PickerSession
}

export function InflowPage({ venue }: InflowPageProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [folderPickerSource, setFolderPickerSource] = useState<IngestSource | null>(null)

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ['ingest-sources', venue.id],
    queryFn:  () => fetchIngestSources(venue.id),
    staleTime: 30_000,
  })

  const toggleMutation = useMutation({
    mutationFn: ({ sourceId, active }: { sourceId: string; active: boolean }) =>
      toggleSource(sourceId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ingest-sources', venue.id] }),
  })

  async function handleFolderSelected(source: IngestSource, folder: { id: string; name: string; path: string }) {
    console.log('Saving folder:', JSON.stringify(folder))
    const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL
    const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY

    // Use jsonb merge to update only folder fields without touching tokens
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/update_ingest_source_folder`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON,
          'Authorization': `Bearer ${SUPABASE_ANON}`,
        },
        body: JSON.stringify({
          p_source_id:   source.id,
          p_folder_id:   folder.id,
          p_folder_name: folder.name,
          p_folder_path: folder.path,
        })
      }
    )
    if (!res.ok) {
      const err = await res.text()
      console.error('Folder save failed:', err)
      // Fallback: direct update without spreading config
      await fetch(
        `${SUPABASE_URL}/rest/v1/ingest_sources?id=eq.${source.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON,
            'Authorization': `Bearer ${SUPABASE_ANON}`,
            'Prefer': 'return=minimal',
          },
          body: JSON.stringify({
            config: {
              folder_id:    folder.id,
              folder_name:  folder.name,
              folder_path:  folder.path,
              folder_set_at: new Date().toISOString(),
            }
          })
        }
      ).then(r => console.log('Fallback update:', r.status))
    }
    queryClient.invalidateQueries({ queryKey: ['ingest-sources', venue.id] })
    setFolderPickerSource(null)
  }

  // Check if we just returned from an OAuth callback
  useEffect(() => {
    const connectedProvider = sessionStorage.getItem('oauth_connected_provider')
    if (connectedProvider) {
      sessionStorage.removeItem('oauth_connected_provider')
      sessionStorage.removeItem('oauth_connected_source_id')
      queryClient.invalidateQueries({ queryKey: ['ingest-sources', venue.id] })
    }
  }, [])

  const handleLocalConnect = useCallback(async () => {
    const source = await connectLocalFolder(venue.id)
    if (source) queryClient.invalidateQueries({ queryKey: ['ingest-sources', venue.id] })
  }, [venue.id])

  const [syncing, setSyncing] = useState(false)

  const [aiTagging, setAiTagging] = useState(false)

  async function handleAiTag() {
    if (aiTagging) return
    setAiTagging(true)
    try {
      const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL
      const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-photo-tagger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON, 'Authorization': `Bearer ${SUPABASE_ANON}` },
        body: JSON.stringify({ venue_id: venue.id, batch_size: 20 })
      })
      const data = await res.json()
      console.log('AI tag result:', data)
      alert(`${data.message ?? 'Done'}`)
    } catch (e) {
      console.error('AI tag failed:', e)
    }
    setAiTagging(false)
  }

  async function handleSync(sourceType: string) {
    if (syncing) return
    setSyncing(true)
    try {
      const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL
      const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY
      const fn = sourceType === 'onedrive' ? 'onedrive-watcher' : sourceType === 'gdrive' ? 'gdrive-watcher' : 'dropbox-watcher'
      const res = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON, 'Authorization': `Bearer ${SUPABASE_ANON}` },
        body: '{}'
      })
      const data = await res.json()
      console.log('Sync result:', data)
      queryClient.invalidateQueries({ queryKey: ['ingest-sources', venue.id] })
    } catch (e) {
      console.error('Sync failed:', e)
    }
    setSyncing(false)
  }

  const handleToggle = useCallback((sourceId: string, active: boolean) => {
    toggleMutation.mutate({ sourceId, active })
  }, [])

  const sourceByType = (type: SourceType) =>
    sources.find(s => s.source_type === type) ?? null

  const connectedCount = sources.filter(s => s.active).length

  return (
    <>
    {folderPickerSource && (
      <FolderPicker
        source={folderPickerSource}
        onSelect={async (folder) => { await handleFolderSelected(folderPickerSource, folder); navigate(`/venue/${venue.slug ?? 'pacha-ibiza'}/picker`) }}
        onClose={() => setFolderPickerSource(null)}
      />
    )}
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Inflow sources</h2>
        {connectedCount > 0 && (
          <span className="text-xs text-emerald-400 font-medium">{connectedCount} active</span>
        )}
      </div>

      {/* Start picking CTA — hero action, shown first when connected */}
      {sources.some(s => !!(s.config as Record<string,unknown>)?.tokens) && (
        <button type="button"
          onClick={() => navigate(`/venue/${venue.slug ?? 'pacha-ibiza'}/picker`)}
          style={{ width: '100%', padding: '14px 20px', borderRadius: '14px',
                   background: 'white', color: 'black', fontWeight: 700,
                   fontSize: '15px', border: 'none', cursor: 'pointer',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          Start picking →
        </button>
      )}

      {/* Connector cards — compact */}
      <div className="space-y-2">
        {PROVIDERS.map(provider => (
          <ConnectorCard
            key={provider.type}
            provider={provider}
            source={isLoading ? null : sourceByType(provider.type)}
            venueId={venue.id}
            venueSlug={venue.slug ?? 'pacha-ibiza'}
            onLocalConnect={handleLocalConnect}
            onToggle={handleToggle}
            onChooseFolder={setFolderPickerSource}
            onSync={() => handleSync(provider.type)}
            onAiTag={handleAiTag}
            loading={isLoading}
          />
        ))}
      </div>
    </div>
    </>
  )
}
